'use client';

import { useCallback, useEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type PointerEvent } from 'react';
import Link from 'next/link';
import { getImageProps } from 'next/image';
import UiIcon from '@/components/ui/UiIcon';
import { ArrowDownIcon, ArrowUpIcon, ArrowUpRightIcon } from '@phosphor-icons/react';
import LabScene from './LabScene';
import LabBackgroundDog from './LabBackgroundDog';
import HomeLabFooter from './HomeLabFooter';
import HeroProjectNavigation from './HeroProjectNavigation';
import DirectionContent, { DirectionNews, WallLighting } from './DirectionContent';
import { directions } from './directions';
import { getChapterOffset, getHomeLabState, HOME_LAB_CHAPTERS } from './homeLabState';
import { attachScrollSettler, createChapterScroll } from './homeLabScroll';
import styles from './home-lab.module.css';

// Оптимизатор сохраняет геометрию фона, но отдаёт WebP/AVIF вместо полноразмерного PNG.
const roomImages = Object.fromEntries(Object.entries({
  day: 'lab-interior-v1', dawn: 'lab-interior-dawn-v2', night: 'lab-interior-night-v2',
  'equipment-day': 'lab-equipped-v2', 'equipment-dawn': 'lab-equipped-dawn-v1', 'equipment-night': 'lab-equipped-night-v1',
}).map(([key, file]) => {
  const { props } = getImageProps({ src: `/assets/home-lab-3d/${file}.png`, alt: '', width: 1536, height: 1024 });
  return [`--room-${key}`, `url("${props.src}")`];
})) as CSSProperties;

const leadership = [
  { label: 'И. о. ректора КБГУ', href: 'https://kbsu.ru/rektor-kbgu/' },
  { label: 'Директор института', href: '/users/15' },
  { label: 'Заведующий лабораторией', href: '/users/5' },
];

export default function HomeLabExperience() {
  const railRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLElement>(null);
  const scrollRef = useRef<ReturnType<typeof attachScrollSettler> | null>(null);
  const [state, setState] = useState(() => getHomeLabState(0, 1));
  const [compact, setCompact] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(true);

  useEffect(() => {
    const small = window.matchMedia('(max-width: 760px), (max-height: 620px), (orientation: portrait)');
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => { setCompact(small.matches); setReducedMotion(reduced.matches); };
    update();
    small.addEventListener('change', update);
    reduced.addEventListener('change', update);
    return () => { small.removeEventListener('change', update); reduced.removeEventListener('change', update); };
  }, []);

  useEffect(() => {
    if (compact) return;
    let frame = 0;
    const read = () => {
      const rail = railRef.current, stage = stageRef.current;
      if (!rail || !stage) return { offset: 0, travel: 0 };
      const bounds = rail.getBoundingClientRect();
      return { offset: -bounds.top, travel: bounds.height - stage.getBoundingClientRect().height };
    };
    const update = () => {
      frame = 0;
      const { offset, travel } = read();
      const next = getHomeLabState(offset, travel);
      setState(previous => previous.progress === next.progress ? previous : next);
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
    const motion = createChapterScroll(window, reducedMotion);
    const settler = attachScrollSettler(railRef.current!, read, offset => {
      if (document.hidden) return;
      const position = read();
      motion.to(Math.max(0, window.scrollY - position.offset + offset), position.travel / (HOME_LAB_CHAPTERS - 1));
    }, schedule, motion.cancel);
    scrollRef.current = settler;
    const observer = new ResizeObserver(settler.refresh);
    if (railRef.current) observer.observe(railRef.current);
    if (stageRef.current) observer.observe(stageRef.current);
    schedule();
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      scrollRef.current = null;
      settler.dispose();
      motion.cancel();
    };
  }, [compact, reducedMotion]);

  const goTo = useCallback((chapter: number) => {
    const index = Math.max(0, Math.min(HOME_LAB_CHAPTERS - 1, chapter));
    const rail = railRef.current;
    const stage = stageRef.current;
    if (!rail || !stage) return;
    if (compact) {
      setState(getHomeLabState(index, HOME_LAB_CHAPTERS - 1));
      window.scrollTo({ top: Math.max(0, window.scrollY + rail.getBoundingClientRect().top), behavior: 'instant' });
      return;
    }
    const bounds = rail.getBoundingClientRect();
    scrollRef.current?.navigate(getChapterOffset(index, bounds.height - stage.getBoundingClientRect().height));
  }, [compact]);

  const direction = state.chapter > 0 ? directions[state.chapter - 1] : null;
  const isLastChapter = state.chapter === HOME_LAB_CHAPTERS - 1;
  const onNextDirection = () => goTo(isLastChapter ? 0 : state.chapter + 1);
  const modelAt = (index: number) => index ? directions[index - 1].id : 'hero' as const;
  const scrollDriven = !compact && !reducedMotion;
  const onNavigationKey = (event: KeyboardEvent<HTMLElement>) => {
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    const focused = (event.target as HTMLElement).closest<HTMLButtonElement>('button[data-chapter-index]');
    const current = Number(focused?.dataset.chapterIndex ?? state.chapter);
    let target: number;
    if (event.key === 'Home') target = 0;
    else if (event.key === 'End') target = HOME_LAB_CHAPTERS - 1;
    else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') target = current - 1;
    else if (event.key === 'ArrowDown' || event.key === 'ArrowRight') target = current + 1;
    else return;
    if ((current === 0 && (event.key === 'ArrowUp' || event.key === 'Home'))
      || (current === HOME_LAB_CHAPTERS - 1 && (event.key === 'ArrowDown' || event.key === 'End'))) return;
    event.preventDefault();
    target = Math.max(0, Math.min(HOME_LAB_CHAPTERS - 1, target));
    goTo(target);
    event.currentTarget.querySelector<HTMLButtonElement>(`button[data-chapter-index="${target}"]`)?.focus({ preventScroll: true });
  };
  const onNavigationLeave = (event: PointerEvent<HTMLElement>) => {
    const focused = event.currentTarget.querySelector<HTMLButtonElement>('button:focus');
    // Клик не оставляет rail раскрытой после ухода мыши; клавиатурный фокус сохраняем.
    if (event.pointerType === 'mouse' && focused && !focused.matches(':focus-visible')) focused.blur();
  };

  return (
    <div ref={railRef} className={styles.rail} id="directions">
      <section ref={stageRef} className={styles.stage} data-home-chapter={direction?.id || 'hero'} data-motion-paused={reducedMotion} data-scroll-driven={scrollDriven} style={{ '--scene-layout': direction ? 1 : 0 } as CSSProperties} aria-label="Лаборатория и направления">
        <div className={styles.heroBackdrop}>
          <div className={styles.roomPlate} style={roomImages}>
            {directions.map((item, index) => <div key={item.id} className={styles.roomEquipment} data-room-layer={item.id} data-room-visible={state.chapter > index} aria-hidden="true" />)}
            <WallLighting />
            <DirectionNews direction={direction?.id || 'achievements'} />
            <LabBackgroundDog />
          </div>
        </div>
        <div className={styles.stageContent}>
          <span className={styles.srOnly} role="status">{direction ? `Направление: ${direction.title}` : 'ЛИСАД — главная сцена'}</span>
          <div className={styles.topLine}>
            {direction ? <header className={styles.directionIntro}>
              <p>{direction.title}</p>
              <h1>{direction.slogan}</h1>
            </header> : <Link href="/about#university" className={styles.university}>КБГУ <span>им. Х. М. Бербекова</span></Link>}
            <span className={styles.stageNumber}>{String(state.chapter + 1).padStart(2, '0')} <span>/ 05</span></span>
          </div>

          <div className={`${styles.sceneFrame} ${direction ? styles.sceneAside : ''}`}>
            <LabScene model={scrollDriven ? modelAt(state.toChapter) : direction?.id || 'hero'} transitionFrom={scrollDriven ? modelAt(state.fromChapter) : undefined} paused={false} reducedMotion={reducedMotion} transitionProgress={state.transitionProgress}
              onHeroNavigate={theme => goTo(directions.findIndex(item => item.id === theme) + 1)} />
          </div>

          {!direction ? (
            <>
              <a className={styles.institute} href="https://kbsu.ru/podrazdelenija/instituty/institut-informatiki-elektroniki-i-kompyuternyh-tehnologij/" target="_blank" rel="noopener noreferrer">Институт электроники, робототехники<br className={styles.desktopBreak} /> и искусственного интеллекта <ArrowUpRightIcon size={16} aria-hidden="true" /></a>
              <div className={styles.heroCopy}>
                <h1><span className={styles.srOnly}>ЛИСАД — </span>Лаборатория интеллектуальных систем<br className={styles.desktopBreak} /> и анализа данных</h1>
                <button type="button" onClick={() => goTo(1)} className={styles.explore}>Исследовать направления <ArrowDownIcon size={22} weight="light" aria-hidden="true" /></button>
              </div>
              <HeroProjectNavigation onDirection={goTo} />
              <div className={styles.leadership}>{leadership.map(role => <Link href={role.href} key={role.label} target={role.href.startsWith('/') ? undefined : '_blank'} rel={role.href.startsWith('/') ? undefined : 'noopener noreferrer'}>{role.label}<ArrowUpRightIcon size={16} aria-hidden="true" /></Link>)}</div>
            </>
          ) : (
            <>
              <DirectionContent direction={direction.id} title={direction.title} />
            </>
          )}

          <nav className={styles.chapterNav} aria-label="Выбор направления" aria-describedby="chapter-navigation-hint" onKeyDown={onNavigationKey} onPointerLeave={onNavigationLeave}>
            <span id="chapter-navigation-hint" className={styles.srOnly}>Клавиши со стрелками — выбор направления. Home — начало, End — последнее направление.</span>
            <button type="button" className={styles.navArrow} onClick={() => goTo(state.chapter - 1)} disabled={state.chapter === 0} aria-label="Предыдущее направление"><ArrowUpIcon size={18} aria-hidden="true" /></button>
            <div className={styles.chapterButtons}>
              <button type="button" data-chapter-index={0} tabIndex={!direction ? 0 : -1} aria-label="Начало — LISAD" aria-current={!direction ? 'step' : undefined} onClick={() => goTo(0)}><span className={styles.chapterTitle}>ЛИСАД</span><span className={styles.chapterTick} aria-hidden="true" /><span className={styles.chapterIcon}><UiIcon name="Cube" /></span></button>
              {directions.map((item, index) => <button type="button" key={item.id} data-chapter-index={index + 1} tabIndex={direction?.id === item.id ? 0 : -1} aria-label={item.title} onClick={() => goTo(index + 1)} aria-current={direction?.id === item.id ? 'step' : undefined}><span className={styles.chapterTitle}>{item.title}</span><span className={styles.chapterTick} aria-hidden="true" /><span className={styles.chapterIcon}><UiIcon name={item.icon} /></span></button>)}
            </div>
            <button type="button" className={styles.navArrow} onClick={onNextDirection} aria-label={isLastChapter ? 'Вернуться к началу — LISAD' : 'Следующее направление'}>
              {isLastChapter ? <ArrowUpIcon size={18} aria-hidden="true" /> : <ArrowDownIcon size={18} aria-hidden="true" />}
            </button>
          </nav>
        </div>
      </section>
      {direction?.id === 'software' && <HomeLabFooter railRef={railRef} />}
    </div>
  );
}
